import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Lock, PlayCircle, CircleDot } from 'lucide-react';
import { learningApi, LearningCourse as Course, LearningFeature } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import YouTubeLearner, { LearnerProgress } from '../components/Learning/YouTubeLearner';
import { mins } from './MyLearning';

/** One course: its features in order, and the video of the one being learned. */
const LearningCourse: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [course, setCourse] = useState<(Course & { features: LearningFeature[] }) | null>(null);
  const [current, setCurrent] = useState<number | null>(null);
  const [live, setLive] = useState<LearnerProgress | null>(null);

  const load = useCallback(async (pick = false) => {
    try {
      const c = (await learningApi.course(Number(id))).data;
      setCourse(c);
      if (pick) {
        // Open the first feature not learned yet (or the first one).
        const next = c.features.find(f => f.status !== 'learned' && !f.locked) || c.features[0];
        setCurrent(next ? next.id : null);
      }
    } catch (e: any) { showError('Could not open the course', e?.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { load(true); }, [load]);

  const feature = course?.features.find(f => f.id === current) || null;
  const idx = course && feature ? course.features.indexOf(feature) : -1;
  const next = course && idx >= 0 ? course.features[idx + 1] : undefined;

  const onLearned = () => {
    showSuccess('Learned', next ? `"${feature?.title}" done — the next feature is unlocked.` : 'You have finished this course.');
    load();
  };

  if (!course) return <div className="p-6 text-sm text-gray-400">Loading…</div>;
  const learnedCount = course.features.filter(f => f.status === 'learned').length;

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => navigate('/learning')} className="p-2 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50" title="My Learning"><ArrowLeft className="h-4 w-4" /></button>
          <h1 className="text-lg font-semibold text-gray-800 flex-1">{course.title}</h1>
          <span className="text-sm text-gray-500">{learnedCount} of {course.features.length} learned</span>
        </div>
        <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
          <div className="bg-white rounded-lg shadow p-3">
            {feature ? (
              <>
                {/* key: a different feature gets a fresh player and a new sitting */}
                <YouTubeLearner key={feature.id} featureId={feature.id} youtubeId={feature.youtube_id} onProgress={setLive} onLearned={onLearned} />
                <div className="mt-3 flex flex-wrap items-start gap-2">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">{idx + 1}. {feature.title}</div>
                    {feature.note && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{feature.note}</div>}
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {(live?.learned || feature.status === 'learned')
                      ? <span className="text-emerald-700 font-semibold">Learned</span>
                      : <span>In progress — watch to the end to finish</span>}
                    <div>{mins(live?.watched_seconds ?? feature.watched_seconds)} watched{(live?.duration || feature.duration_sec) ? ` · video ${mins(live?.duration || feature.duration_sec)}` : ''}</div>
                  </div>
                </div>
                {next && !next.locked && (live?.learned || feature.status === 'learned') && (
                  <button onClick={() => { setLive(null); setCurrent(next.id); }} className="mt-3 px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700">Next: {next.title} →</button>
                )}
              </>
            ) : <div className="p-8 text-center text-sm text-gray-400">This course has no features yet.</div>}
          </div>

          <div className="bg-white rounded-lg shadow p-2 h-fit">
            <div className="text-[11px] font-bold uppercase text-gray-500 px-2 py-1">Features</div>
            {course.features.map((f, i) => {
              const active = f.id === current;
              const Icon = f.status === 'learned' ? CheckCircle2 : f.locked ? Lock : f.status === 'in_progress' ? CircleDot : PlayCircle;
              return (
                <button key={f.id} disabled={f.locked} onClick={() => { setLive(null); setCurrent(f.id); }}
                  className={`w-full text-left flex items-start gap-2 px-2 py-2 rounded text-sm ${active ? 'bg-blue-50' : 'hover:bg-gray-50'} disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={f.locked ? 'Learn the earlier features first' : ''}>
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${f.status === 'learned' ? 'text-emerald-600' : f.status === 'in_progress' ? 'text-amber-500' : 'text-gray-400'}`} />
                  <span className="flex-1">
                    <span className={active ? 'font-semibold text-blue-800' : 'text-gray-800'}>{i + 1}. {f.title}</span>
                    <span className="block text-[11px] text-gray-400">
                      {f.duration_sec ? mins(f.duration_sec) : ''}{f.status === 'in_progress' ? ` · ${mins(f.watched_seconds)} watched` : ''}{f.status === 'learned' && f.completed_at ? ` · learned ${String(f.completed_at).slice(0, 10).split('-').reverse().join('-')}` : ''}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningCourse;
